import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, addMonths, parseISO } from 'date-fns';

const { FiX, FiFileText, FiShield, FiZap, FiInfo, FiCheck } = FiIcons;

const LeaseModal = ({ isOpen, onClose, onSave, tenants, properties, initialData }) => {
  const [formData, setFormData] = useState({
    propertyId: '',
    tenantId: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(addMonths(new Date(), 3), 'yyyy-MM-dd'),
    rentAmount: '',
    depositAmount: '',
    depositStatus: 'Held',
    status: 'Draft',
    utilities: [],
    terms: ''
  });

  const utilityOptions = ['Electricity', 'Water', 'Internet', 'Gas', 'Trash', 'Parking'];

  useEffect(() => {
    const defaults = {
      propertyId: '',
      tenantId: '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: format(addMonths(new Date(), 3), 'yyyy-MM-dd'),
      rentAmount: '',
      depositAmount: '',
      depositStatus: 'Held',
      status: 'Draft',
      utilities: [],
      terms: ''
    };
    if (initialData) {
      setFormData({ ...defaults, ...initialData });
    } else {
      setFormData(defaults);
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handlePropertyChange = (pid) => {
    const prop = properties.find(p => p.id === pid);
    setFormData(prev => ({
      ...prev, 
      propertyId: pid,
      rentAmount: prop?.rentRate || ''
    }));
  };

  const toggleUtility = (util) => {
    setFormData(prev => ({
      ...prev,
      utilities: prev.utilities.includes(util) 
        ? prev.utilities.filter(u => u !== util)
        : [...prev.utilities, util]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      rentAmount: Number(formData.rentAmount),
      depositAmount: Number(formData.depositAmount)
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in duration-200">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiFileText} size={20} />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900">{initialData?.id ? 'Modify Lease' : 'Draft New Lease'}</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Midterm Contract Generator</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <SafeIcon icon={FiX} size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-8">
          {/* Property & Tenant Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Subject Property</label>
              <select 
                required
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 font-bold text-sm"
                value={formData.propertyId}
                onChange={e => handlePropertyChange(e.target.value)}
              >
                <option value="">Select Property...</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Assign Tenant</label>
              <select 
                required
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 font-bold text-sm"
                value={formData.tenantId}
                onChange={e => setFormData({...formData, tenantId: e.target.value})}
              >
                <option value="">Select Tenant...</option>
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates & Financials */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-1">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Move-In</label>
              <input 
                type="date"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold"
                value={formData.startDate}
                onChange={e => setFormData({...formData, startDate: e.target.value})}
              />
            </div>
            <div className="col-span-1">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Move-Out</label>
              <input 
                type="date"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold"
                value={formData.endDate}
                onChange={e => setFormData({...formData, endDate: e.target.value})}
              />
            </div>
            <div className="col-span-1">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Monthly Rent</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                <input 
                  type="number"
                  className="w-full pl-7 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold"
                  value={formData.rentAmount}
                  onChange={e => setFormData({...formData, rentAmount: e.target.value})}
                />
              </div>
            </div>
            <div className="col-span-1">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Security Deposit</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                <input 
                  type="number"
                  className="w-full pl-7 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold"
                  value={formData.depositAmount}
                  onChange={e => setFormData({...formData, depositAmount: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Utilities */}
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Utilities Included in Rent</label>
            <div className="flex flex-wrap gap-2">
              {utilityOptions.map(util => (
                <button
                  key={util}
                  type="button"
                  onClick={() => toggleUtility(util)}
                  className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-tight transition-all border ${
                    formData.utilities.includes(util)
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-400 border-gray-100 hover:border-indigo-200'
                  }`}
                >
                  {util}
                </button>
              ))}
            </div>
          </div>

          {/* Terms & Conditions */}
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Special Terms / Addendums</label>
            <textarea 
              rows="3"
              className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g. Weekly cleaning included, No overnight guests for more than 3 days..."
              value={formData.terms}
              onChange={e => setFormData({...formData, terms: e.target.value})}
            />
          </div>

          {/* Status & Security */}
          <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiShield} className="text-indigo-600" />
              <div>
                <p className="text-xs font-black text-indigo-900 uppercase">Contract Status</p>
                <p className="text-[10px] text-indigo-600 font-bold">This will be archived in the tenant vault.</p>
              </div>
            </div>
            <select 
              className="bg-white border-none rounded-xl px-4 py-2 text-xs font-black text-indigo-700 outline-none shadow-sm"
              value={formData.status}
              onChange={e => setFormData({...formData, status: e.target.value})}
            >
              <option value="Draft">Draft</option>
              <option value="Signed">Signed (Pending Start)</option>
              <option value="Active">Active</option>
            </select>
          </div>
        </form>

        <div className="p-6 border-t border-gray-100 bg-white flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-3 font-bold text-gray-400 hover:text-gray-600 transition">
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            className="px-10 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 flex items-center gap-2"
          >
            <SafeIcon icon={FiCheck} /> {initialData?.id ? 'Update Lease' : 'Generate Lease'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaseModal;